import { useEffect, useState, type FormEvent } from 'react'
import { ApiHttpError } from '~/lib/api/client'
import type {
  Holiday,
  HolidayCreatePayload,
  HolidayUpdatePayload,
} from '~/lib/api/types'
import styles from './holidays-page.module.scss'

type Props = {
  initial?: Holiday
  onSubmit: (
    payload: HolidayCreatePayload | HolidayUpdatePayload
  ) => Promise<unknown>
  onCancel: () => void
  pending: boolean
  error: ApiHttpError | null
}

export function HolidayForm({
  initial,
  onSubmit,
  onCancel,
  pending,
  error,
}: Props) {
  const [startDate, setStartDate] = useState(initial?.start_date ?? '')
  const [endDate, setEndDate] = useState(initial?.end_date ?? '')
  const [name, setName] = useState(initial?.name ?? '')
  const [note, setNote] = useState(initial?.note ?? '')

  useEffect(() => {
    if (initial) {
      setStartDate(initial.start_date)
      setEndDate(initial.end_date)
      setName(initial.name)
      setNote(initial.note ?? '')
    }
  }, [initial])

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    onSubmit({
      start_date: startDate,
      end_date: endDate,
      name: name.trim(),
      note: note.trim() === '' ? null : note.trim(),
    })
  }

  const heading = initial ? 'Edit holiday' : 'Add holiday'

  return (
    <form className={styles.formCard} onSubmit={submit} aria-label={heading}>
      <div className={styles.formGrid}>
        <label className={styles.field}>
          <span className={styles.label}>Start date</span>
          <input
            type="date"
            className={styles.input}
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            required
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>End date</span>
          <input
            type="date"
            className={styles.input}
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            required
          />
        </label>
      </div>
      <label className={styles.field}>
        <span className={styles.label}>Name</span>
        <input
          type="text"
          className={styles.input}
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={120}
          required
        />
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Note (optional)</span>
        <input
          type="text"
          className={styles.input}
          value={note}
          onChange={e => setNote(e.target.value)}
          maxLength={240}
        />
      </label>
      {error && (
        <p className={styles.error} role="alert">
          {formatError(error)}
        </p>
      )}
      <div className={styles.formActions}>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={onCancel}
          disabled={pending}
        >
          Cancel
        </button>
        <button type="submit" className={styles.primaryBtn} disabled={pending}>
          {pending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  )
}

function formatError(err: ApiHttpError): string {
  const body = err.body as { error?: { message?: string } } | null
  const msg = body?.error?.message
  if (msg) return msg
  if (err.status === 409) {
    return 'Synced from Google Calendar; manage it there.'
  }
  return 'Could not save the holiday.'
}
